import { useState } from "react";
import { X, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmt = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };

export function WeeklyDistributionModal({ data, monthly, onClose, onConfirm, busy }) {
  const [weeks, setWeeks] = useState(data.weeks.map((w) => ({ ...w })));
  const sumO = weeks.reduce((a, w) => a + (Number(w.orders_target) || 0), 0);
  const sumS = weeks.reduce((a, w) => a + (Number(w.sales_target) || 0), 0);
  const targetO = Number(monthly.orders_target || 0);
  const targetS = Math.round(Number(monthly.sales_target || 0));
  const ok = sumO === targetO && sumS === targetS;
  const setVal = (i, k, v) => setWeeks(weeks.map((w, j) => (j === i ? { ...w, [k]: v.replace(/\D/g, "") } : w)));
  const payload = () => weeks.map((w) => ({ week_index: w.week_index, orders_target: Number(w.orders_target) || 0, sales_target: Number(w.sales_target) || 0 }));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="weekly-dist-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Distribución semanal — {data.month}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="weekly-dist-close"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-600">
          Meta mensual: <b>{targetO}</b> reparaciones · <b>${targetS}</b>. Reparto proporcional a los días de cada semana; puedes editar los valores antes de guardar.
        </p>
        {data.has_existing && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2" data-testid="weekly-dist-existing-warning">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            Este técnico ya tiene metas semanales en este mes. Elige si deseas conservarlas o redistribuirlas.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">Semana</th>
                <th className="text-right px-2 py-2 hidden sm:table-cell">Días</th>
                <th className="text-right px-2 py-2">Órdenes</th>
                <th className="text-right px-2 py-2">$ Meta</th>
                {data.has_existing && <th className="text-right px-2 py-2">Actual</th>}
              </tr>
            </thead>
            <tbody>
              {weeks.map((w, i) => (
                <tr key={w.week_index} className="border-t border-slate-100" data-testid={`weekly-dist-row-${w.week_index}`}>
                  <td className="px-2 py-1.5 font-medium whitespace-nowrap">
                    S{w.week_index}
                    <div className="font-mono text-[11px] text-slate-500 font-normal">{fmt(w.start)}–{fmt(w.end)} <span className="sm:hidden">· {w.days}d</span></div>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono hidden sm:table-cell">{w.days}</td>
                  <td className="px-2 py-1.5 text-right">
                    <input inputMode="numeric" className="input-base font-mono text-right w-20 py-1" value={w.orders_target} onChange={(e) => setVal(i, "orders_target", e.target.value)} data-testid={`weekly-dist-orders-${w.week_index}`} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input inputMode="numeric" className="input-base font-mono text-right w-24 py-1" value={w.sales_target} onChange={(e) => setVal(i, "sales_target", e.target.value)} data-testid={`weekly-dist-sales-${w.week_index}`} />
                  </td>
                  {data.has_existing && (
                    <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-500">
                      {w.existing_orders_target != null ? `${w.existing_orders_target} · $${Math.round(w.existing_sales_target)}` : "—"}
                    </td>
                  )}
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200 font-semibold">
                <td className="px-2 py-2">Suma semanal</td>
                <td className="hidden sm:table-cell" />
                <td className={`px-2 py-2 text-right font-mono ${sumO === targetO ? "text-emerald-700" : "text-rose-600"}`} data-testid="weekly-dist-sum-orders">{sumO}</td>
                <td className={`px-2 py-2 text-right font-mono ${sumS === targetS ? "text-emerald-700" : "text-rose-600"}`} data-testid="weekly-dist-sum-sales">${sumS}</td>
                {data.has_existing && <td />}
              </tr>
            </tbody>
          </table>
        </div>
        <div className={`text-sm flex items-center gap-2 ${ok ? "text-emerald-700" : "text-rose-600"}`} data-testid="weekly-dist-sum-status">
          {ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {ok ? "Las semanas suman exactamente la meta mensual." : `La suma difiere de la meta mensual (${sumO - targetO >= 0 ? "+" : ""}${sumO - targetO} órdenes, ${sumS - targetS >= 0 ? "+" : ""}$${sumS - targetS}).`}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 justify-end">
          <button className="btn-secondary py-2.5" disabled={busy} onClick={() => onConfirm(null)} data-testid="weekly-dist-keep">
            {data.has_existing ? "Conservar semanas actuales" : "Guardar solo meta mensual"}
          </button>
          <button className="btn-primary py-2.5" disabled={busy} onClick={() => onConfirm(payload())} data-testid="weekly-dist-confirm">
            {data.has_existing ? "Redistribuir y guardar" : "Confirmar y guardar semanas"}
          </button>
        </div>
      </div>
    </div>
  );
}
